/**
 * CJdropshipping vs 竞品专页面 — 共享 React 组件
 * 主页 + 全部子页面共用的公共 Section 组件。
 *
 * 导出组件：
 *   - CdnHostContext   — CDN 域名 Context
 *   - ConstantsContext — 文案常量 Context
 *   - isMobileDevice   — 判断移动端宽度
 *   - getCookie        — 读取 Cookie
 *   - handleCtaClick   — CTA 按钮点击（登录态跳转）
 *   - Kicker           — 小标签组件
 *   - SellpointsBar    — 服务卖点横排标签
 *   - FitSection       — "Who Should Choose" 左图右文
 *   - AdvantagesSection — 核心优势卡片网格
 *   - TestimonialSection — 用户评价双行跑马灯
 *   - BottomCta        — 深色背景底部 CTA
 *
 * 样式依赖：./SharedComponents.less（各页面 index.less 统一 import）
 *
 * Context 使用方式：
 *   父组件 <CdnHostContext.Provider value={cdnHost}>
 *           <ConstantsContext.Provider value={constants}>
 *   子组件内 useContext(CdnHostContext)、useContext(ConstantsContext)
 */

import React, { createContext, useContext, useCallback, useState } from 'react';

// ========== Context ==========
export const CdnHostContext = createContext('');
export const ConstantsContext = createContext({});

// ========== 工具函数 ==========

/**
 * 判断是否为移动端（宽度 ≤ 700px）
 * @returns {boolean}
 */
export function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 700;
}

/**
 * 读取 Cookie
 * 【注意】使用 cjLoginToken 作为登录凭证，与 baseController.getLoginToken() 一致
 * @param {string} name
 * @returns {string|null}
 */
export function getCookie(name) {
  if (typeof document === 'undefined') return null;
  var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * CTA 按钮点击处理
 * 【注意】未登录 → /login.html?target=base64(目标URL)；
 *        已登录 PC → 跳目标页；已登录 Mobile → setShowModal(true)（当前页面暂不使用 Modal）
 * @param {string} url - 目标 URL
 * @param {Function} [setShowModal]
 */
export function handleCtaClick(url, setShowModal) {
  if (typeof window === 'undefined') return;
  var token = getCookie('cjLoginToken');
  if (!token) {
    var targetUrl = window.location.origin + url;
    window.location.href = '/login.html?target=' + btoa(targetUrl);
    return;
  }
  window.location.href = url;
}

// ========== Kicker ==========

/**
 * 小标签组件，带橙色脉冲圆点
 * 【CSS Modules 兼容】必须传入 styles 才能获得正确的 hash class name
 * @param {{ text: string, styles: object, className?: string }} props
 */
export function Kicker({ text, styles: localStyles, className }) {
  // 优先使用 CSS Modules hash class（styles['kicker']），否则降级为字面量 'kicker'
  const cls = localStyles && localStyles['kicker'] ? localStyles['kicker'] : 'kicker';
  return (
    <span className={[cls, className].filter(Boolean).join(' ')}>
      <i />
      {text}
    </span>
  );
}

// ========== SellpointsBar ==========

/**
 * 服务卖点横排标签栏
 * 从 ConstantsContext.SELLPOINTS 读取 string[] 渲染 tags
 */
export function SellpointsBar({ styles }) {
  const { SELLPOINTS } = useContext(ConstantsContext);
  return (
    <section className={styles['sellpoints']}>
      <div className={styles['sellpoints-wrap']}>
        {(SELLPOINTS || []).map(function (label, i) {
          return (
            <span key={i} className={styles['sellpoint']}>
              <span className={styles['check']}>✓</span>
              {label}
            </span>
          );
        })}
      </div>
    </section>
  );
}

// ========== FitSection ==========

/**
 * "Who Should Choose CJdropshipping" 左图右文布局
 * 从 ConstantsContext.FIT 读取文案
 *
 * FIT 结构：
 *   { kicker, title, desc, badgeText, floatTitle, floatDesc,
 *     items: [{ icon, text }],
 *     summaryTitle, summaryDesc, imageUrl }
 */
export function FitSection({ styles }) {
  const { FIT } = useContext(ConstantsContext);
  if (!FIT) return null;
  return (
    <section className={styles['fit']}>
      <div className={styles['fit-container']}>
        {/*
         * 【纠正】原版 DOM 结构：fit-visual（图片）在第一列，kicker/title/desc/items 在第二列。
         * 之前错误地将 kicker/title/desc 提到 fit-grid 外部（section-header），
         * 导致手机端顺序变为：标题→图片→列表，与原版（图片→标题→列表）不符。
         * 现已还原：section-header 移除，kicker/title/desc 移入 fit-content 右列。
         * top-badge 也还原到 fit-img 内部（原版位置）。
         */}
        <div className={styles['fit-grid']}>
          {/* 左侧图片列 — DOM 第一，手机端显示在上方 */}
          <div className={styles['fit-visual']}>
            <div className={styles['fit-img']}>
              {/* 【纠正】top-badge 位于 fit-img 内部，与原版 DOM 一致 */}
              {FIT.badgeText && (
                <span className={styles['top-badge']}>
                  <span>⭐</span>
                  {FIT.badgeText}
                </span>
              )}
              <img
                src={FIT.imageUrl}
                alt="Who Should Choose CJdropshipping"
                loading="lazy"
              />
            </div>
            {(FIT.floatTitle || FIT.floatDesc) && (
              <div className={styles['fit-float']}>
                {FIT.floatTitle && <h3>{FIT.floatTitle}</h3>}
                {FIT.floatDesc && <p>{FIT.floatDesc}</p>}
              </div>
            )}
          </div>
          {/* 右侧文字列 — 含 kicker/title/desc/items，手机端显示在图片下方 */}
          <div className={styles['fit-content']}>
            <Kicker text={FIT.kicker} styles={styles} />
            <h2
              className={styles['section-title']}
              dangerouslySetInnerHTML={{ __html: FIT.title }}
            />
            {FIT.desc && (
              <p className={styles['section-desc']} style={{margin: '0 0 26px'}}>{FIT.desc}</p>
            )}
            <ul className={styles['fit-list']}>
              {(FIT.items || []).map(function (item, i) {
                // 【纠正】兼容两种格式：string（cjVsZendrop）和 { text, icon }（alternatives 主页）
                var text = typeof item === 'string' ? item : item.text;
                var icon = typeof item === 'object' && item ? item.icon : null;
                return (
                  <li key={i} className={styles['fit-item']}>
                    {/* 【纠正】原版 fit-item 始终有 <span class="check">✓</span>，不依赖 data.icon */}
                    <span className={styles['check']}>{icon || '✓'}</span>
                    <span>{text}</span>
                  </li>
                );
              })}
            </ul>
            {(FIT.summaryTitle || FIT.summaryDesc) && (
              <div className={styles['fit-summary']}>
                {FIT.summaryTitle && <h3>{FIT.summaryTitle}</h3>}
                {FIT.summaryDesc && <p>{FIT.summaryDesc}</p>}
              </div>
            )}
          </div>
        </div>
        {/* 原版 footer-cta：在 fit-grid 之后，fit-container 内，橙色渐变卡片 */}
        {/* {(FIT.footerCtaText || FIT.footerCtaBtn) && (
          <div className={styles['footer-cta']}>
            {FIT.footerCtaText && <p>{FIT.footerCtaText}</p>}
            {FIT.footerCtaBtn && (
              <button
                className={styles['btn']}
                onClick={function () { handleCtaClick(FIT.footerCtaUrl || '#get-started'); }}
              >
                {FIT.footerCtaBtn}
              </button>
            )}
          </div>
        )} */}
      </div>
    </section>
  );
}

// ========== AdvantagesSection ==========

/**
 * 核心优势卡片网格（8张，PC 4列，Tablet 2列，Mobile 1列）
 * 从 ConstantsContext.ADVANTAGES 读取文案
 *
 * ADVANTAGES 结构：
 *   { kicker, title, desc,
 *     cards: [{ num, title, desc, imageUrl }],
 *     footerCta?: { text, ctaText, ctaUrl } }
 */
export function AdvantagesSection({ styles, onCtaClick }) {
  const { ADVANTAGES } = useContext(ConstantsContext);
  if (!ADVANTAGES) return null;
  return (
    <section className={styles['adv-section']}>
      <div className={styles['adv-container']}>
        <div className={styles['section-header']}>
          <Kicker text={ADVANTAGES.kicker} styles={styles} />
          <h2
            className={styles['section-title']}
            dangerouslySetInnerHTML={{ __html: ADVANTAGES.title }}
          />
          {ADVANTAGES.desc && (
            <p className={styles['section-desc']}>{ADVANTAGES.desc}</p>
          )}
        </div>
        <div className={styles['adv-grid']}>
          {(ADVANTAGES.cards || []).map(function (card, i) {
            return (
              <div key={i} className={styles['adv-card']}>
                <div className={styles['adv-img']}>
                  <img
                    src={card.imageUrl}
                    alt={card.title}
                    loading="lazy"
                  />
                  <span className={styles['num']}>{card.num}</span>
                </div>
                <div className={styles['adv-body']}>
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
        {ADVANTAGES.footerCta && (
          <div className={styles['adv-footer-cta']}>
            <p>{ADVANTAGES.footerCta.text}</p>
            <button
              className={styles['btn']}
              onClick={function () {
                if (onCtaClick) onCtaClick(ADVANTAGES.footerCta.ctaUrl);
              }}
            >
              {ADVANTAGES.footerCta.ctaText}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ========== TestimonialSection ==========

/**
 * 用户评价双行无限滚动跑马灯
 * 【注意】marquee 动画依赖 SharedComponents.less 中的 altMarqueeLeft/altMarqueeRight
 *        hover 暂停通过 CSS animation-play-state:paused 实现（在 less 中声明）
 *
 * 从 ConstantsContext.TESTIMONIALS 读取文案：
 *   { kicker, title, desc,
 *     rows: [[{ name, role, country, avatarUrl, quote, date }], [...]] }
 *   rows[0] → 左滚行，rows[1] → 右滚行
 *   每行数据需重复 2 次以确保视觉无缝衔接
 */
export function TestimonialSection({ styles }) {
  const { TESTIMONIALS } = useContext(ConstantsContext);
  if (!TESTIMONIALS) return null;

  function renderRow(cards, direction) {
    // 重复一次以实现视觉无缝
    const doubled = cards.concat(cards);
    return (
      <div className={styles['marquee-inner']}>
        {doubled.map(function (card, i) {
          return (
            <div key={i} className={styles['t-card']}>
              <div className={styles['t-card-header']}>
                {card.avatarUrl ? (
                  <img
                    className={styles['avatar']}
                    src={card.avatarUrl}
                    alt={card.name}
                    loading="lazy"
                  />
                ) : (
                  <span className={styles['avatar']}>
                    {card.name ? card.name[0] : '?'}
                  </span>
                )}
                <div className={styles['t-meta']}>
                  <h3>{card.name}</h3>
                  <p className={styles['role']}>{card.role}</p>
                  {card.country && (
                    <span className={styles['country']}>{card.country}</span>
                  )}
                </div>
              </div>
              {/* 【纠正】兼容 quote（alternatives 主页）和 text（cjVsZendrop 等子页）两种字段名 */}
              <p className={styles['t-text']}>{card.quote || card.text}</p>
              {card.date && (
                <p className={styles['t-bottom']}>{card.date}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <section className={styles['testimonial']}>
      <div className={styles['testimonial-container']}>
        <div className={styles['section-header']}>
          <Kicker text={TESTIMONIALS.kicker} styles={styles} />
          <h2
            className={styles['section-title']}
            dangerouslySetInnerHTML={{ __html: TESTIMONIALS.title }}
          />
          {TESTIMONIALS.desc && (
            <p className={styles['section-desc']}>{TESTIMONIALS.desc}</p>
          )}
        </div>
        <div className={styles['marquee-wrap']}>
          {(TESTIMONIALS.rows || []).map(function (row, i) {
            const direction = i % 2 === 0 ? 'row-left' : 'row-right';
            return (
              <div
                key={i}
                className={[styles['marquee-row'], styles[direction]].join(' ')}
              >
                {renderRow(row, direction)}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ========== BottomCta ==========

/**
 * 深色背景底部 CTA
 * 从 ConstantsContext.BOTTOM_CTA 读取文案
 * 背景图通过 CSS backgroundImage 内联样式传入（图片链接来自 constants.js）
 *
 * BOTTOM_CTA 结构：
 *   { kicker, title, desc, ctaText, ctaUrl, bgImage }
 */
export function BottomCta({ styles, onCtaClick }) {
  const { BOTTOM_CTA } = useContext(ConstantsContext);
  if (!BOTTOM_CTA) return null;

  const bgStyle = BOTTOM_CTA.bgImage
    ? {
        backgroundImage: `linear-gradient(135deg, rgba(16,16,16,0.76), rgba(16,16,16,0.48)), url('${BOTTOM_CTA.bgImage}')`,
      }
    : {};

  return (
    <section className={styles['bottom-cta']} style={bgStyle} id="explore-cj-service">
      <div className={styles['bottom-cta-inner']}>
        {BOTTOM_CTA.kicker && <Kicker text={BOTTOM_CTA.kicker} styles={styles} />}
        <h2
          className={styles['bottom-title']}
          dangerouslySetInnerHTML={{ __html: BOTTOM_CTA.title }}
        />
        {BOTTOM_CTA.desc && (
          <p className={styles['bottom-desc']}>{BOTTOM_CTA.desc}</p>
        )}
        {BOTTOM_CTA.ctaText && (
          <button
            className={styles['btn']}
            onClick={function () {
              if (onCtaClick) onCtaClick(BOTTOM_CTA.ctaUrl || '/home');
            }}
          >
            {BOTTOM_CTA.ctaText}
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: '18px'}}>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
          </button>
        )}
      </div>
    </section>
  );
}
